import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { createRef, useCallback, useEffect, useState } from "react";
import { MAX_UNCOMPRESSED_SIZE_MB, RecordType } from "./RecordPage";
import IconValidate from "../../assets/icons/icon-thumb-up.svg";
import { loggerBuilder } from "../../services/logger";
import { Profile } from "../../services/profile/profileModel";
import { FFmpegLoadError, useRecorder } from "../../services/recorderService";
import VideoPlayer from "../../components/VideoPlayer";
import SubmitButton from "../../components/SubmitButton";
import { thumbnailOfVideo } from "../../react-helpers/blob";
import {
  createProfileUploadLinkRequest,
  notifyUploadSuccessRequest,
} from "../../services/profile/profileApi";
import { Capsule } from "../../services/capsule/capsuleModel";
import {
  createCapsuleUploadLinkRequest,
  notifyCapsuleUploadSuccessRequest,
} from "../../services/capsule/capsuleApi";
import { cx } from "../../react-helpers/css";
import { profileService } from "../../services/profile/profileService";
import { capsuleService } from "../../services/capsule/capsuleService";

const MAX_VIDEO_COUNT = 4;
const logger = loggerBuilder("review-component");

const ReviewComponent = ({
  type,
  videos,
  profileId,
  capsuleId,
  setShowReview,
  clearVideos,
}: {
  type: RecordType;
  videos: [string, Blob][];
  profileId?: Profile["id"];
  capsuleId?: Capsule["id"];
  setShowReview(value: boolean): void;
  clearVideos(): void;
}) => {
  const { notifyProfileUploadError } = profileService();
  const { notifyCapsuleUploadError } = capsuleService();
  const { onUpload, onCompress, ffmpegPercent, stopRecorder } = useRecorder()!;
  const { t } = useTranslation(["record"]);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [choosenVideo, setChoosenVideo] = useState<string | null>(null);
  const [failedToUploadMeta, setFailedToUploadMeta] = useState<{
    thumbnail?: string;
    duration?: number;
  } | null>(null);

  useEffect(() => {
    stopRecorder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function preventPageRefresh(event: BeforeUnloadEvent) {
      event.preventDefault();
      // eslint-disable-next-line deprecation/deprecation
      event.returnValue = "";
      return "";
    }

    window.addEventListener("beforeunload", preventPageRefresh);
    return () => {
      window.removeEventListener("beforeunload", preventPageRefresh);
    };
  }, []);

  const onSubmitVideo = useCallback(
    async (video: Blob, videoRef?: HTMLVideoElement | null) => {
      setSubmitting(true);

      let thumbnail = failedToUploadMeta?.thumbnail;
      if (videoRef) {
        logger.info("Generating thumbnail");
        // A missing thumbnail must not prevent the video from being saved
        thumbnail = await thumbnailOfVideo(videoRef).catch((e) => {
          logger.error(e);
          return undefined;
        });
      }
      const duration = videoRef?.duration ?? failedToUploadMeta?.duration;

      let uploadId: string | undefined;
      try {
        logger.info("Starting upload process");
        // The backend only switches to the new video once it has checked
        // the file is on S3, so success is only reported after that
        const upload = async () => {
          if (type === "profile") {
            ({ uploadId } = await onUpload(
              () => createProfileUploadLinkRequest(profileId!),
              video,
              duration,
            ));
            await notifyUploadSuccessRequest(profileId!, uploadId, thumbnail);
          } else if (type === "capsule") {
            ({ uploadId } = await onUpload(
              () => createCapsuleUploadLinkRequest(capsuleId!),
              video,
              duration,
            ));
            await notifyCapsuleUploadSuccessRequest(
              capsuleId!,
              uploadId,
              thumbnail,
            );
          } else throw new Error("Invalid record type");
        };

        await toast.promise(upload(), {
          loading: t("record:upload.SAVING"),
          success: t("record:upload.SUCCESS"),
          error: t("record:upload.ERROR"),
        });
      } catch (e) {
        setFailedToUploadMeta({ thumbnail, duration });

        // Removes the unconfirmed file, the previous video is left untouched
        if (type === "profile")
          await notifyProfileUploadError(profileId!, uploadId);
        else if (type === "capsule")
          await notifyCapsuleUploadError(capsuleId!, uploadId);

        logger.error(e);
        setSubmitting(false);
        return;
      }

      return type === "profile"
        ? navigate(`/candidate/profiles/${profileId}`)
        : type === "capsule"
          ? navigate(`/organization/capsules/${capsuleId}`)
          : navigate("/");
    },
    [
      capsuleId,
      failedToUploadMeta,
      navigate,
      onUpload,
      profileId,
      t,
      type,
      notifyProfileUploadError,
      notifyCapsuleUploadError,
    ],
  );

  return (
    <div
      className={cx([
        "layout_content",
        type === "profile" && "--bg--candidate",
        type === "capsule" && "--bg--organization",
      ])}
    >
      <div className="page-content container --wrap--m">
        <div className="page_head">
          <h1 className="page_title --txt--center">
            {t("record:review.CHOOSE_TAKE")}
          </h1>
        </div>
        {failedToUploadMeta && choosenVideo && (
          <div className="info --warning cblock">
            {t("record:upload.FAILED_INFO")}
            <br />
            <SubmitButton
              type="button"
              className="btn--2"
              onClick={() => {
                const video = videos.find(([id]) => id === choosenVideo)![1];
                return onSubmitVideo(video);
              }}
            >
              {t("record:upload.RETRY")}
            </SubmitButton>
          </div>
        )}
        <div className="lblock--l grid-2">
          {videos.map(([id, video]) => {
            const ref = createRef<HTMLVideoElement>();
            return (
              <div key={id}>
                <div className="video-preview">
                  <VideoPlayer video={video} ref={ref} />
                  {choosenVideo === id &&
                    ffmpegPercent !== null &&
                    ffmpegPercent < 100 && (
                      <div className="encoding-status">
                        {t("record:review.ENCODING", {
                          percent: ffmpegPercent,
                        })}
                      </div>
                    )}

                  <SubmitButton
                    type="button"
                    className="btn-validate-video"
                    disabled={submitting}
                    onClick={async () => {
                      setChoosenVideo(id);
                      const videoRef = ref.current;

                      setSubmitting(true);
                      try {
                        logger.info("Starting compress process");
                        await onCompress(video, ref.current?.duration);
                      } catch (e) {
                        logger.error(e);
                        toast.error(
                          e instanceof FFmpegLoadError
                            ? t("record:review.FFMPEG_LOAD_FAILED")
                            : t("record:review.COMPRESSION_FAILED"),
                        );
                        setSubmitting(false);
                        return;
                      }
                      return onSubmitVideo(video, videoRef);
                    }}
                  >
                    <img src={IconValidate} className="icon" />
                    {t("record:review.VALIDATE_TAKE")}
                  </SubmitButton>
                </div>
                {video.size > MAX_UNCOMPRESSED_SIZE_MB * 1024 * 1024 && (
                  <div className="body--30 --strong">
                    {t("record:review.COMPRESSION_NOTICE", {
                      size: (video.size / 1024 / 1024).toFixed(2),
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {videos.length < MAX_VIDEO_COUNT && (
            <button
              className="card-new --video"
              onClick={() => setShowReview(false)}
            >
              <div className="card_image_container">
                <canvas className="card_image_bg" width="16" height="9" />
              </div>
              <div className="card_body">{t("record:review.RETRY_TAKE")}</div>
            </button>
          )}
        </div>
        <div className="lblock--l --txt--center">
          <button
            className="btn--2"
            onClick={() => {
              clearVideos();
              setShowReview(false);
            }}
          >
            {t("record:review.RESTART_ALL")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewComponent;
